// ── Throttle API types ──────────────────────────────────────────────────────
// Types for the read-only PlayCDU Throttle API (https://api.playcdu.co/throttle).
// It serves cached snapshots (minute-fresh) of per-server performance, anti-lag
// "chunk throttling" data and staff-triggered performance dumps. All timestamps
// are epoch milliseconds. Shapes match schema `v: 2` — note that the anti-lag
// config document has grown new rule kinds *within* v2, so treat unknown
// entries as skippable rather than assuming a `v` bump announces every change.

/** The envelope schema version these types were written against. */
export const THROTTLE_SCHEMA_VERSION = 2;

/**
 * Standard cache envelope wrapping most stored values.
 * `fetchedAt` is when the backend poller wrote the record to its cache —
 * render staleness from it, not from the request time.
 */
export interface CacheEnvelope<T> {
  /** Schema version, currently 2. A bump means shapes changed. */
  v: number;
  fetchedAt: number;
  data: T;
}

export type ThrottleHealthState =
  | 'awaiting_first_poll'
  | 'healthy'
  | 'stale'
  | 'failing';

/** Response of GET /v1/health (returned with HTTP 200 when fresh, 503 when stale). */
export interface ThrottleHealth {
  ok: boolean;
  state: ThrottleHealthState;
  lastSuccessAt: number | null;
  lastAttemptAt: number | null;
  lastError: string | null;
  lastErrorAt: number | null;
  consecutiveFailures: number;
  pollCount: number;
  fleetSize: number | null;
  lastDiscoveryAt: number | null;
}

/**
 * One summary row of the fleet index (GET /v1/servers) and `status` of the
 * detail routes. While `online` is false all perf/flagged fields hold
 * last-known values from `lastSeenAt` — gate "live" styling on `online`.
 * Offline rows are pruned from the index after ~24 h.
 */
export interface ThrottleServerSummary {
  online: boolean;
  /** Epoch ms when the server's heartbeat was last seen. */
  lastSeenAt: number | null;
  /** Capped at 20.0 — prefer `mspt` for load ranking. */
  tps: number | null;
  /** Uncapped ms-per-tick; the tick budget is 50 ms. */
  mspt: number | null;
  playerCount: number | null;
  maxPlayers: number | null;
  /** Live flagged feed: 0 = nothing flagged (healthy). Capped at the 10 worst per dimension. */
  flaggedChunkCount: number;
  /** Highest MSPT among flagged chunks; `null` (not 0) when nothing is flagged. */
  worstFlaggedChunkMspt: number | null;
  /** Start of the current flagged episode; `null` when clear. Stable for the whole episode. */
  flaggedSince: number | null;
  /** Internal change-detection bookkeeping — do not render. */
  throttlingHash: string | null;
  /** Live (non-expired) cached performance dumps. */
  dumpCount: number;
  /** When the newest dump was taken; `null` when there are none. */
  lastDumpAt: number | null;
  worstDumpChunkMspt: number | null;
  /** Epoch ms when the full perf record was last cached. */
  perfWrittenAt: number | null;
  /** Quasar cluster id (e.g. "BCG"). */
  clusterId: string | null;
  /** Quasar server id within the cluster (e.g. "US", "EU"). */
  serverId: string | null;
  /**
   * Population throttle currently in force server-wide (the highest matching
   * `type: "PC"` rule), or `null` while the server is below every tier.
   * Resolved by the API — do not re-derive it from the config document.
   */
  serverWideThrottle: ServerWideThrottle | null;
}

/** Map of FQDN → summary row (GET /v1/servers `data`). */
export type ThrottleServerIndex = Record<string, ThrottleServerSummary>;

export interface ThrottlePerfPlayer {
  UUID?: string;
  name?: string;
}

/**
 * Full perf record published by a game server (`perf.data` of the detail
 * routes). Every field may be absent — the worker passes through whatever the
 * game wrote. `timestamp` is when the game server took the measurement.
 */
export interface ThrottlePerfData {
  tps?: number;
  mspt?: number;
  entities?: number;
  chunks?: number;
  playerCount?: number;
  maxPlayers?: number;
  redisPing?: number;
  timestamp?: number;
  players?: ThrottlePerfPlayer[];
  fqdn?: string;
  serverId?: string;
  clusterId?: string;
  modPackName?: string;
  motd?: string;
  mcVersion?: string;
  quasarVersion?: string;
  modloader?: string;
  modloaderVersion?: string;
  serverPort?: number;
  hostname?: string;
}

/**
 * One sampled chunk (flagged feed or performance dump). Any field may be
 * absent. `posX`/`posZ` are CHUNK coordinates, not block coordinates
 * (multiply by 16; center ≈ pos × 16 + 8).
 */
export interface ChunkSample {
  dimension?: string;
  posX?: number;
  posZ?: number;
  /** Average ms/tick this chunk cost in the last ~60 s sampling window. */
  mspt?: number;
}

/**
 * Live flagged feed (`throttling.data` of the detail routes): the ≤10 worst
 * currently problematic chunks per dimension, sorted worst-first. An empty
 * array means nothing is flagged — the normal, healthy state.
 */
export interface ThrottlingFeed {
  problematicChunks: ChunkSample[];
}

/** Server-wide snapshot taken at dump time. */
export interface DumpAnalytics {
  tps?: number;
  mspt?: number;
  entities?: number;
  chunks?: number;
  playerCount?: number;
}

/**
 * One full staff-triggered performance dump (`/q chunkPerformance dumpData`).
 * Immutable once cached; expires 24 h after it was taken.
 */
export interface PerformanceDump {
  /** EVERY sampled chunk, slowest first — can be hundreds/thousands of rows. */
  chunks: ChunkSample[];
  analytics: DumpAnalytics | null;
}

/** Dump metadata row (GET /v1/servers/:fqdn/dumps and GET /v1/dumps). */
export interface DumpIndexEntry {
  /** Dump counter — the `:n` path segment of the full-dump route. */
  n: number;
  /** When the worker cached it. */
  cachedAt: number;
  /** When it was taken on the server. */
  dumpedAt: number | null;
  chunkCount: number;
  worstChunkMspt: number | null;
  analytics: DumpAnalytics | null;
}

/** Fleet-wide dump metadata, keyed by FQDN (GET /v1/dumps `data`). */
export type DumpsIndex = Record<string, DumpIndexEntry[]>;

/** Response of GET /v1/servers/:fqdn/dumps (metadata only, plain object). */
export interface ServerDumpsResponse {
  fqdn: string;
  count: number;
  entries: DumpIndexEntry[];
}

/** The `dumps` slice of the merged single-server view. */
export interface ThrottleServerDumps {
  count: number;
  lastDumpAt: number | null;
  worstChunkMspt: number | null;
  /** Newest full dump, or `null` when there are none. */
  latest: CacheEnvelope<PerformanceDump> | null;
}

/**
 * Merged single-server view (GET /v1/servers/:fqdn).
 * `status` and the slices are each independently nullable — render partial
 * cards (e.g. a server with cached dumps but a pruned index entry).
 */
export interface ThrottleServerDetail {
  fqdn: string;
  status: ThrottleServerSummary | null;
  perf: CacheEnvelope<ThrottlePerfData> | null;
  throttling: CacheEnvelope<ThrottlingFeed> | null;
  dumps: ThrottleServerDumps;
}

/** Response of GET /v1/servers/:clusterId (segment without dots; case-insensitive). */
export interface ThrottleClusterResponse {
  clusterId: string;
  serverCount: number;
  servers: {
    fqdn: string;
    status: ThrottleServerSummary | null;
    perf: CacheEnvelope<ThrottlePerfData> | null;
    throttling: CacheEnvelope<ThrottlingFeed> | null;
    /** No `latest` here — full tables are too big to embed per node. */
    dumps: Omit<ThrottleServerDumps, 'latest'>;
  }[];
}

/** Fields every anti-lag rule shares. */
export interface ThrottleRuleBase {
  id: string;
  /**
   * `target:all`, `target:cluster:<clusterId>` or `target:single:<fqdn>` —
   * the most specific target level wins, per rule type.
   */
  target: string;
  /** Tick-rate multiplier applied when the rule bites (×1 = warn only, ×0.1 = 10% speed). */
  throttleMulti: number;
}

/**
 * One per-chunk anti-lag tier (`type: "MSPT"`). Several tiers can share a
 * target: they escalate (higher `thresholdMSPT` with a harsher
 * `throttleMulti`), plus dedicated `foreloadedOnly` tiers for force-loaded
 * chunks.
 */
export interface MsptThrottleConfig extends ThrottleRuleBase {
  /** Absent on documents written before the API tagged rule types. */
  type?: 'MSPT';
  /** Rule applies only to force-loaded chunks (API spelling is "foreloaded"). */
  foreloadedOnly: boolean;
  /** Percentage component of the flagging trigger (e.g. 99.9). */
  thresholdPercentage: number;
  /** Absolute floor in ms/tick a chunk must exceed for this tier to apply. */
  thresholdMSPT: number;
  /** WARNING → THROTTLED grace period in game ticks (1200 ≈ 1 min at 20 TPS). */
  throttleDelayTicks: number;
}

/**
 * A population throttle as reported on a server's `status`: while the server
 * holds at least `minPlayerCount` players, everything ticks at
 * `throttleMulti`. There is no per-chunk threshold and no grace period.
 */
export interface ServerWideThrottle extends ThrottleRuleBase {
  /** Player count at which this tier starts applying. */
  minPlayerCount: number;
}

/** The same population throttle as it appears in the config document. */
export interface PlayerCountThrottleConfig extends ServerWideThrottle {
  type: 'PC';
}

/**
 * One entry of `chunkThrottleConfigs`, discriminated by `type`:
 * `MSPT` for a per-chunk tier, `PC` for a server-wide population throttle
 * (untagged entries predate the discriminator and are MSPT tiers). Switch on
 * it before reading anything beyond `ThrottleRuleBase` — the two variants
 * share no other fields (see `isMsptThrottleConfig` /
 * `isPlayerCountThrottleConfig`).
 */
export type ChunkThrottleConfig = MsptThrottleConfig | PlayerCountThrottleConfig;

/**
 * Population-based mob-AI throttle: once the server holds `minPlayerCount`
 * players, mobs further than `playerDistanceChunks` chunks from any player
 * stop ticking their AI. Tiers escalate — the highest matching one wins.
 */
export interface MobAiThrottleConfig {
  id: string;
  target: string;
  /** Player count at which this tier starts applying. */
  minPlayerCount: number;
  /** Radius in chunks around each player within which mob AI keeps ticking. */
  playerDistanceChunks: number;
}

/** Per-player, per-server placement cap for a block. */
export interface BlockLimit {
  target: string;
  blockId: string;
  limit: number;
}

/** Network-wide anti-lag configuration document (GET /v1/config `data`). */
export interface AntiLagData {
  /** Mixed list of per-chunk MSPT tiers and server-wide population throttles. */
  chunkThrottleConfigs: ChunkThrottleConfig[];
  blockLimits: BlockLimit[];
  /** Absent on documents written before mob-AI throttling was configurable. */
  mobAIThrottleConfigs?: MobAiThrottleConfig[];
  /**
   * Per-player shadow throttles. The entry shape is not documented yet, so it
   * is kept opaque rather than guessed at — only its length is meaningful here.
   */
  playerShadowThrottleList?: unknown[];
}
